import React, { useEffect, useState } from 'react';
import useTrackToolUsage from '@/hooks/useTrackToolUsage';
import { Link } from 'react-router-dom';
import { api } from '@/App';
import { toast } from 'sonner';
import { TrendUp, TrendDown, Wallet, PiggyBank, Compass, Calendar, ArrowRight } from '@phosphor-icons/react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import MilestoneCelebration from '@/components/MilestoneCelebration';
import HelpHint from '@/components/HelpHint';
import BetaFeedbackButton from '@/components/BetaFeedbackButton';
import BetaFeedbackBanner from '@/components/BetaFeedbackBanner';
import { useMilestoneCheck } from '@/hooks/useMilestoneCheck';
import { track } from '@/lib/analytics';

const CALENDLY_URL = 'https://calendly.com/sbusbygfi/outsource-solution-strategies';

const Dashboard = () => {
  useTrackToolUsage('dashboard');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { celebrations, dismissAll } = useMilestoneCheck(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#4A5568]">Loading dashboard...</div>
      </div>
    );
  }

  const mockChartData = [
    { month: 'Jan', income: stats?.monthly_income || 0, expenses: stats?.monthly_expenses || 0 },
    { month: 'Feb', income: stats?.monthly_income || 0, expenses: (stats?.monthly_expenses || 0) * 0.9 },
    { month: 'Mar', income: stats?.monthly_income || 0, expenses: (stats?.monthly_expenses || 0) * 1.1 },
    { month: 'Apr', income: stats?.monthly_income || 0, expenses: (stats?.monthly_expenses || 0) * 0.95 },
    { month: 'May', income: stats?.monthly_income || 0, expenses: (stats?.monthly_expenses || 0) * 1.05 },
    { month: 'Jun', income: stats?.monthly_income || 0, expenses: stats?.monthly_expenses || 0 },
  ];

  const StatCard = ({ title, value, icon: Icon, trend, color, sub }) => (
    <div className="kpi-tile group" data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}10`, color }}>
          <Icon size={20} weight="duotone" />
        </div>
        {trend !== undefined && (
          <div className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
            trend >= 0 ? 'bg-signal-success-soft text-signal-success' : 'bg-signal-danger-soft text-signal-danger'
          }`}>
            {trend >= 0 ? <TrendUp size={12} weight="bold" /> : <TrendDown size={12} weight="bold" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <p className="eyebrow mt-2">{title}</p>
      <p className="kpi-tile-value">{value}</p>
      {sub && <p className="kpi-tile-sub">{sub}</p>}
    </div>
  );

  return (
    <div className="space-y-8 fade-in">
      {celebrations.length > 0 && (
        <MilestoneCelebration
          celebrations={celebrations}
          onClose={dismissAll}
          onAcknowledgeAll={dismissAll}
        />
      )}
      {/* Executive header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow text-brand-gold mb-2">Executive Dashboard</p>
          <div className="flex items-center gap-3">
            <h1 className="cabinet-grotesk text-4xl tracking-tight font-medium text-brand-navy">
              Financial Intelligence Overview
            </h1>
            <HelpHint
              topic="What this Dashboard shows"
              query="dashboard overview platform"
              testid="dashboard-help-hint"
            />
          </div>
          <p className="text-[15px] text-ink-soft mt-1.5">
            Live snapshot of income, expenses, savings, and retirement readiness — updated in real time as you enter data.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link
            to="/getting-started"
            data-testid="dashboard-getting-started-link"
            className="inline-flex items-center gap-2 px-4 py-2 border border-surface-border text-brand-navy text-sm font-medium rounded-lg bg-white hover:bg-surface-subtle transition-colors shadow-fluent-sm"
          >
            <Compass size={16} weight="duotone" />
            Getting Started
          </Link>
          <BetaFeedbackButton testid="dashboard-beta-feedback-button" source="dashboard" />
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="dashboard-book-consultation-link"
            onClick={() => track('consultation_clicked', { source: 'dashboard' })}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-navy text-white text-sm font-medium rounded-lg hover:bg-brand-navy-elevated transition-colors shadow-fluent"
          >
            <Calendar size={16} weight="duotone" />
            Book Consultation
          </a>
        </div>
      </div>

      <BetaFeedbackBanner />

      {/* KPI Grid — bento layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          title="Monthly Income"
          value={`$${stats.monthly_income.toLocaleString()}`}
          icon={Wallet}
          color="#0A1128"
          sub="Aggregated from Income Sources"
        />
        <StatCard
          title="Monthly Expenses"
          value={`$${stats.monthly_expenses.toLocaleString()}`}
          icon={TrendDown}
          color="#DC2626"
          sub="Current month, all categories"
        />
        <StatCard
          title="Savings Rate"
          value={`${stats.savings_rate.toFixed(1)}%`}
          icon={PiggyBank}
          trend={stats.savings_rate > 20 ? 15 : -5}
          color="#16A34A"
          sub="(Income − Expenses) ÷ Income"
        />
        <StatCard
          title="Net Cash Flow"
          value={`$${stats.net_cash_flow.toLocaleString()}`}
          icon={TrendUp}
          color="#C5A059"
          sub="Per month, after expenses"
        />
      </div>

      {/* Retirement Snapshot */}
      {stats.total_retirement > 0 ? (
        <div className="card-elevated" data-testid="dashboard-retirement-snapshot">
          <div className="card-header">
            <div className="w-8 h-8 rounded-md bg-brand-gold/12 flex items-center justify-center">
              <PiggyBank size={16} weight="duotone" className="text-brand-gold-hover" />
            </div>
            <div className="flex-1">
              <h2 className="cabinet-grotesk text-lg font-medium text-brand-navy">Retirement Snapshot</h2>
              <p className="text-xs text-ink-soft">Aggregated across all retirement account buckets</p>
            </div>
            <Link to="/net-worth" className="text-xs text-brand-gold-hover font-semibold uppercase tracking-[0.08em] hover:text-brand-navy transition-colors">
              Edit balances <ArrowRight size={12} weight="bold" className="inline ml-0.5" />
            </Link>
          </div>
          <div className="card-body">
            <div className="flex items-baseline gap-3 mb-5">
              <p className="text-4xl font-medium cabinet-grotesk text-brand-navy tabular-nums">
                ${stats.total_retirement.toLocaleString()}
              </p>
              <span className="text-xs text-ink-soft uppercase tracking-wider">Total retirement assets</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="retirement-breakdown">
              {[
                ['traditional_401k', 'Traditional 401(k)'],
                ['traditional_ira', 'Traditional IRA'],
                ['roth_401k', 'Roth 401(k)'],
                ['roth_ira', 'Roth IRA'],
                ['taxable_brokerage', 'Taxable Brokerage'],
              ].map(([key, label]) => {
                const value = (stats.retirement_breakdown || {})[key] || 0;
                return (
                  <div key={key} className="rounded-lg border border-surface-border-subtle bg-surface-subtle px-4 py-3" data-testid={`retirement-bucket-${key}`}>
                    <p className="text-[10px] uppercase tracking-[0.12em] text-ink-soft font-semibold">{label}</p>
                    <p className={`text-[15px] font-medium mt-1.5 tabular-nums ${value > 0 ? 'text-brand-navy' : 'text-ink-faint'}`}>
                      ${value.toLocaleString()}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-surface-border bg-surface-subtle/60 p-8 text-center" data-testid="dashboard-retirement-empty">
          <p className="text-sm text-ink-muted mb-3">
            <strong className="text-brand-navy">No retirement balances yet.</strong> Add 401(k) / IRA / Roth balances on the Net Worth page so they appear here.
          </p>
          <Link to="/net-worth" className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.08em] text-brand-navy hover:text-brand-gold-hover transition-colors">
            Go to Net Worth <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      )}

      {/* Missing-data prompts */}
      {stats.missing_data && (stats.missing_data.income || stats.missing_data.expenses) && (
        <div data-testid="dashboard-missing-data-banner" className="rounded-xl border border-brand-gold/30 bg-brand-gold/[0.06] p-5">
          <p className="text-sm text-brand-navy font-medium mb-2">Complete your setup to see accurate cash flow</p>
          <ul className="text-xs text-ink-muted space-y-1 ml-4 list-disc">
            {stats.missing_data.income && <li><Link to="/income-sources" className="text-brand-gold-hover underline hover:text-brand-navy">Add an income source</Link> — your monthly income drives savings rate and net cash flow.</li>}
            {stats.missing_data.expenses && <li><Link to="/spending-tracker" className="text-brand-gold-hover underline hover:text-brand-navy">Log at least one expense this month</Link> — required to compute net cash flow and emergency-fund coverage.</li>}
          </ul>
        </div>
      )}

      {/* Financial Health Score */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="card-elevated lg:col-span-1" data-testid="financial-health-score">
          <div className="card-header">
            <div className="w-8 h-8 rounded-md bg-brand-gold/12 flex items-center justify-center">
              <TrendUp size={16} weight="duotone" className="text-brand-gold-hover" />
            </div>
            <div className="flex-1">
              <h2 className="cabinet-grotesk text-lg font-medium text-brand-navy">Financial Health</h2>
              <p className="text-xs text-ink-soft">Composite readiness score</p>
            </div>
          </div>
          <div className="card-body flex items-center gap-6">
            <div className="relative w-32 h-32 flex-shrink-0">
              <svg className="transform -rotate-90 w-32 h-32">
                <circle cx="64" cy="64" r="56" stroke="#E2E8F0" strokeWidth="8" fill="none"/>
                <circle
                  cx="64" cy="64" r="56"
                  stroke="#C5A059" strokeWidth="8" fill="none"
                  strokeDasharray={`${2 * Math.PI * 56}`}
                  strokeDashoffset={`${2 * Math.PI * 56 * (1 - stats.financial_health_score / 100)}`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-medium cabinet-grotesk text-brand-navy tabular-nums">{stats.financial_health_score}</span>
                <span className="text-[10px] uppercase tracking-[0.14em] text-ink-soft mt-0.5">of 100</span>
              </div>
            </div>
            <div className="flex-1 space-y-2.5">
              <div className="flex justify-between text-xs">
                <span className="text-ink-soft">Savings Rate</span>
                <span className="font-medium text-brand-navy">{stats.savings_rate > 20 ? 'Excellent' : stats.savings_rate > 10 ? 'Good' : 'Needs Focus'}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-ink-soft">Emergency Fund</span>
                <span className="font-medium text-brand-navy">In Progress</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-ink-soft">Retirement Planning</span>
                <span className="font-medium text-brand-navy">Active</span>
              </div>
              <Link to="/wealth-health" className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-brand-gold-hover hover:text-brand-navy transition-colors">
                Detailed breakdown <ArrowRight size={11} weight="bold" />
              </Link>
            </div>
          </div>
        </div>

        {/* Income vs Expenses Chart */}
        <div className="card-elevated lg:col-span-2" data-testid="income-expenses-chart">
          <div className="card-header">
            <div className="w-8 h-8 rounded-md bg-brand-navy/8 flex items-center justify-center">
              <Wallet size={16} weight="duotone" className="text-brand-navy" />
            </div>
            <div className="flex-1">
              <h2 className="cabinet-grotesk text-lg font-medium text-brand-navy">Cash Flow — 6-Month Trend</h2>
              <p className="text-xs text-ink-soft">Illustrative; based on current monthly figures</p>
            </div>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={mockChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0A1128" stopOpacity={0.24}/>
                    <stop offset="95%" stopColor="#0A1128" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#C5A059" stopOpacity={0.28}/>
                    <stop offset="95%" stopColor="#C5A059" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" stroke="#94A3B8" style={{ fontSize: '11px' }} axisLine={false} tickLine={false} />
                <YAxis stroke="#94A3B8" style={{ fontSize: '11px' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: 'rgba(255,255,255,0.95)', border: '1px solid #E2E8F0', borderRadius: '10px', boxShadow: '0 8px 24px -4px rgba(15,23,42,0.08)', fontSize: '12px' }}
                  labelStyle={{ color: '#0F172A', fontWeight: 600 }}
                />
                <Area type="monotone" dataKey="income" name="Income" stroke="#0A1128" strokeWidth={2} fillOpacity={1} fill="url(#colorIncome)" />
                <Area type="monotone" dataKey="expenses" name="Expenses" stroke="#C5A059" strokeWidth={2} fillOpacity={1} fill="url(#colorExpenses)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;